import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import { Alert, Box, Divider, Grid, Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { DateField } from "components/Forms/DateField";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import {
  CreateEpisodicEventInput,
  EpisodicEventItemFragment,
  Issue,
  IssueLevel,
  UpdateEpisodicEventInput,
  WasteEpisodicEventType,
} from "generated-graphql/graphql";
import { useValidatingForm } from "hooks/useValidatingForm";
import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { Control, FormProvider, UseFormSetError } from "react-hook-form";
import { getDateLikeIsoDate, toUtcMidnightIsoFromDateLike } from "util/dates";
import { hasCriticalIssues } from "util/forms";

import {
  CREATE_EPISODIC_EVENT_MUTATION,
  EPISODIC_EVENT_VALIDATOR_QUERY,
  UPDATE_EPISODIC_EVENT_MUTATION,
} from "./api";
import { EVENT_TYPE_LABELS } from "./episodicDisplay";

const TYPE_SELECT_ITEMS = [
  {
    value: WasteEpisodicEventType.Planned,
    display: EVENT_TYPE_LABELS[WasteEpisodicEventType.Planned],
  },
  {
    value: WasteEpisodicEventType.Unplanned,
    display: EVENT_TYPE_LABELS[WasteEpisodicEventType.Unplanned],
  },
];

const TYPE_LOCKED_TOOLTIP =
  "An event's type can't change once declared — close it and declare a new one.";

type EpisodicEventFormValues = {
  type: WasteEpisodicEventType;
  reason: string;
  startDate: string | null;
  endDate: string | null;
  notifiedEpaAt: string | null;
  epaFormReference: string;
};

/** Converts a form date-only value into the UTC-midnight instant the
 * `notifiedEpaAt` DateTime scalar expects, mirroring
 * `WasteContainerDialog`'s `toDateTimeInput` for `estimatedPickupDate`. */
function toDateTimeInput(isoDate: string | null): string | undefined {
  if (!isoDate) {
    return undefined;
  }
  return toUtcMidnightIsoFromDateLike(DateTime.fromISO(isoDate));
}

function toCreateEpisodicEventInput(
  values: EpisodicEventFormValues,
  facilityId: string
): CreateEpisodicEventInput {
  return {
    facilityId,
    type: values.type,
    reason: values.reason.trim(),
    startDate: values.startDate ?? "",
    endDate: values.endDate ?? "",
  };
}

/**
 * `notifiedEpaAt` is only sent when the user actually touched it: re-deriving
 * the stored instant through the browser's local zone (see `toDateTimeInput`)
 * can shift it to a different calendar day for negative-UTC-offset users, the
 * same hazard `WasteContainerDialog` guards against for `estimatedPickupDate`.
 */
function toUpdateEpisodicEventInput(
  values: EpisodicEventFormValues,
  eventId: string,
  notifiedEpaAtDirty: boolean
): UpdateEpisodicEventInput {
  return {
    id: eventId,
    reason: values.reason.trim(),
    startDate: values.startDate ?? undefined,
    endDate: values.endDate ?? undefined,
    epaFormReference: values.epaFormReference.trim() || null,
    notifiedEpaAt: notifiedEpaAtDirty ? (toDateTimeInput(values.notifiedEpaAt) ?? null) : undefined,
  };
}

/**
 * Debounced live validator, mirroring
 * AccumulationAreas/AccumulationAreaDialog's useAccumulationAreaInputValidation.
 * The validator's `EpisodicEventInput` doesn't carry `reason`, so it never
 * flags a blank reason — that's enforced client-side in `onSubmit` instead.
 */
function useEpisodicEventInputValidation(facilityId: string, currentEventId?: string | null) {
  const [query] = useLazyQuery(EPISODIC_EVENT_VALIDATOR_QUERY, { fetchPolicy: "no-cache" });

  return useCallback(
    async (values: EpisodicEventFormValues): Promise<Issue[]> => {
      if (!values.startDate || !values.endDate) {
        return [];
      }
      const { data } = await query({
        variables: {
          input: {
            facilityId,
            type: values.type,
            startDate: values.startDate,
            endDate: values.endDate,
          },
          currentEventId: currentEventId ?? undefined,
        },
      });
      return data?.episodicEventInputValidator ?? [];
    },
    [query, facilityId, currentEventId]
  );
}

/** The validator can't see `reason` at all, so a blank one never surfaces as
 * a server Issue — catch it here before it reaches the mutation. */
function validateReason(
  values: EpisodicEventFormValues,
  setError: UseFormSetError<EpisodicEventFormValues>
): boolean {
  if (values.reason.trim()) {
    return true;
  }
  setError("reason", { type: "manual", message: "Reason is required" });
  return false;
}

/** BAD_USER_INPUT errors surface their (user-safe) server message verbatim;
 * anything else falls back to a generic toast. */
function describeSaveError(error: unknown, fallback: string): string {
  if (!(error instanceof ApolloError)) {
    return fallback;
  }
  const badUserInput = error.graphQLErrors.find((e) => e.extensions?.code === "BAD_USER_INPUT");
  return badUserInput?.message ?? fallback;
}

/**
 * Renders one severity's worth of validator issues verbatim — no
 * state-specific client copy. CRITICAL issues (`severity="error"`) block
 * save via `hasCriticalIssues`; ERROR issues (`severity="warning"`) are
 * informational only. Issue keys like "episodicYearLimit" don't map to a
 * form field, so these render as a standalone list rather than relying on
 * `useValidatingForm`'s per-field error wiring.
 */
function IssueAlerts({
  issues,
  level,
  severity,
}: {
  issues: Issue[];
  level: IssueLevel;
  severity: "error" | "warning";
}) {
  const filtered = issues.filter((issue) => issue.level === level);
  if (filtered.length === 0) {
    return null;
  }
  return (
    <Grid size={{ xs: 12 }}>
      <Stack spacing={1}>
        {filtered.map((issue) => (
          <Alert key={`${issue.key ?? level}-${issue.message}`} severity={severity}>
            {issue.message}
          </Alert>
        ))}
      </Stack>
    </Grid>
  );
}

/** Edit-mode-only recordkeeping for a filing made outside Encamp — the event
 * itself doesn't require EPA notification, but the facility's 40 CFR 262.232
 * notice obligation does, and this is where that gets logged. */
function NotificationRecordFields({ control }: { control: Control<EpisodicEventFormValues> }) {
  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Divider>Notification record</Divider>
        <Typography variant="caption" color="text.secondary">
          For EPA notifications filed outside Encamp
        </Typography>
      </Grid>
      <Grid size={{ xs: 6 }}>
        <DateField name="notifiedEpaAt" label="EPA notified on" control={control} />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <FormTextField name="epaFormReference" label="EPA form reference" control={control} />
      </Grid>
    </>
  );
}

/**
 * Owns the create/update mutations and the save-time validation gate (the
 * client-side reason check, then the server validator's critical-issue gate)
 * so the dialog component itself only wires the result to the Save button.
 */
function useEpisodicEventSave({
  mode,
  facilityId,
  event,
  getValues,
  setError,
  notifiedEpaAtDirty,
  validateInput,
  onClose,
}: {
  mode: "declare" | "edit";
  facilityId: string;
  event: EpisodicEventItemFragment | undefined;
  getValues: () => EpisodicEventFormValues;
  setError: UseFormSetError<EpisodicEventFormValues>;
  notifiedEpaAtDirty: boolean;
  validateInput: (values: EpisodicEventFormValues) => Promise<Issue[]>;
  onClose: () => void;
}) {
  const alerts = useAlerts();

  const [createEvent, { loading: isCreating }] = useMutation(CREATE_EPISODIC_EVENT_MUTATION, {
    refetchQueries: ["FacilityEpisodicEvents", "FacilityAccumulationSummary"],
  });
  const [updateEvent, { loading: isUpdating }] = useMutation(UPDATE_EPISODIC_EVENT_MUTATION, {
    refetchQueries: ["FacilityEpisodicEvents", "FacilityAccumulationSummary"],
  });

  const onSubmit = async () => {
    const values = getValues();
    if (!validateReason(values, setError)) {
      return;
    }
    const submitIssues = await validateInput(values);
    if (hasCriticalIssues(submitIssues)) {
      const firstCritical = submitIssues.find((issue) => issue.level === IssueLevel.Critical);
      alerts.error(firstCritical?.message ?? "Fix the critical issues before saving");
      return;
    }
    if (mode === "declare") {
      await createEvent({
        variables: { input: toCreateEpisodicEventInput(values, facilityId) },
        onCompleted: () => {
          alerts.success("Episodic event declared");
          onClose();
        },
        onError: (error) => {
          alerts.error(describeSaveError(error, "An error occurred while declaring the event"));
        },
      });
      return;
    }
    if (!event) {
      return;
    }
    await updateEvent({
      variables: {
        input: toUpdateEpisodicEventInput(values, event.id, notifiedEpaAtDirty),
      },
      onCompleted: () => {
        alerts.success("Episodic event updated");
        onClose();
      },
      onError: (error) => {
        alerts.error(describeSaveError(error, "An error occurred while updating the event"));
      },
    });
  };

  return { onSubmit, isSaving: isCreating || isUpdating };
}

export type EpisodicEventDialogProps = {
  open: boolean;
  onClose: () => void;
  mode: "declare" | "edit";
  /** Every event belongs to exactly one facility; this locks the create form
   * to it and scopes the "one per type per year" validator check. */
  facilityId: string;
  /** Required in edit mode — the caller already has the full row from the
   * ledger, so this dialog doesn't re-fetch it. */
  event?: EpisodicEventItemFragment;
};

/** The planned-vs-unplanned explainer at the top of the form. */
function TypeExplainer() {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary">
        Planned or unplanned?
      </Typography>
      <InfoIconTooltip
        ariaLabel="Planned vs unplanned episodic events"
        tooltip={
          <Stack spacing={1} sx={{ p: 0.5 }}>
            <Typography variant="caption">
              <strong>Planned</strong> events are known in advance: tank cleanouts, equipment
              maintenance, excess inventory removal, short-term construction. EPA must be notified
              at least 30 days before the event starts.
            </Typography>
            <Typography variant="caption">
              <strong>Unplanned</strong> events are sudden: spills, process upsets, product recalls,
              acts of nature. EPA must be notified within 72 hours.
            </Typography>
            <Typography variant="caption">
              Either way, the event opens a 60-day window to ship its waste, the facility may run
              one of each per calendar year, and its generator status is unchanged.
            </Typography>
          </Stack>
        }
      />
    </Stack>
  );
}

export function EpisodicEventDialog({
  open,
  onClose,
  mode,
  facilityId,
  event,
}: EpisodicEventDialogProps) {
  const defaultValues = useMemo<EpisodicEventFormValues>(
    () => ({
      type: event?.type ?? WasteEpisodicEventType.Planned,
      reason: event?.reason ?? "",
      startDate: event?.startDate ?? null,
      endDate: event?.endDate ?? null,
      notifiedEpaAt: event?.notifiedEpaAt
        ? (getDateLikeIsoDate(event.notifiedEpaAt) ?? null)
        : null,
      epaFormReference: event?.epaFormReference ?? "",
    }),
    [event]
  );

  const validateInput = useEpisodicEventInputValidation(facilityId, event?.id ?? null);
  const form = useValidatingForm<EpisodicEventFormValues>(defaultValues, [], validateInput);
  const {
    control,
    getValues,
    setError,
    issues,
    formState: { isDirty, dirtyFields },
  } = form;

  const { onSubmit, isSaving } = useEpisodicEventSave({
    mode,
    facilityId,
    event,
    getValues,
    setError,
    notifiedEpaAtDirty: Boolean(dirtyFields.notifiedEpaAt),
    validateInput,
    onClose,
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={mode === "declare" ? "Declare episodic event" : "Edit episodic event"}
      primaryActionButtonProps={{
        loading: isSaving,
        onClick: onSubmit,
        disabled: hasCriticalIssues(issues) || !isDirty,
      }}
    >
      <FormProvider {...form}>
        <Box component="form" sx={{ pt: 1 }}>
          <TypeExplainer />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <FormSelect
                name="type"
                label="Type"
                control={control}
                selectItems={TYPE_SELECT_ITEMS}
                disabled={mode === "edit"}
                tooltip={mode === "edit" ? TYPE_LOCKED_TOOLTIP : undefined}
                clearable={false}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormTextField
                name="reason"
                label="Reason"
                control={control}
                textFieldProps={{ required: true, multiline: true, minRows: 2 }}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <DateField name="startDate" label="Start date" control={control} />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <DateField name="endDate" label="End date" control={control} />
            </Grid>
            <IssueAlerts issues={issues} level={IssueLevel.Critical} severity="error" />
            <IssueAlerts issues={issues} level={IssueLevel.Error} severity="warning" />
            {mode === "edit" && <NotificationRecordFields control={control} />}
          </Grid>
        </Box>
      </FormProvider>
    </Dialog>
  );
}
